import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PlayIcon,
  PauseIcon,
  ForwardIcon,
  BackwardIcon,
  ArrowPathIcon,
  XMarkIcon,
  TrashIcon,
  DevicePhoneMobileIcon,
  HandRaisedIcon,
  ArrowsPointingOutIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/solid';
import rumService from '../../../services/api/rum.service';
import sdkService from '../../../services/api/sdk.service';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../../store/hooks';
import { selectCurrentApplication } from '../../../store/slices/application.slice';

interface MobileSessionInfo {
  id: string;
  session_id: string;
  user_id?: string;
  anonymous_id?: string;
  platform: string;
  device_type?: string;
  device_model?: string;
  device_manufacturer?: string;
  os_name?: string;
  os_version?: string;
  app_version?: string;
  screen_width?: number;
  screen_height?: number;
  country?: string;
  city?: string;
  started_at: string;
  ended_at?: string;
  duration_ms?: number;
  screen_view_count: number;
  event_count: number;
  error_count: number;
  crash_count?: number;
}

interface TouchEvent {
  id: string;
  type: 'tap' | 'longPress' | 'swipe' | 'pinch' | 'scroll';
  timestamp: number;
  screenName: string;
  x: number;
  y: number;
  relativeX: number;
  relativeY: number;
  details?: {
    direction?: 'up' | 'down' | 'left' | 'right';
    velocity?: number;
    duration?: number;
    scale?: number;
    scrollDepth?: number;
    elementType?: string;
    elementId?: string;
    elementLabel?: string;
  };
}

interface ScreenEvent {
  id: string;
  type: 'screenView' | 'touch' | 'error' | 'crash' | 'networkRequest';
  timestamp: number;
  title: string;
  screenName?: string;
  details?: any;
  x?: number;
  y?: number;
}

interface TouchHeatmapPoint {
  x: number;
  y: number;
  type: 'tap' | 'longPress' | 'swipe' | 'pinch';
  intensity: number;
  screenName: string;
}

interface ScrollDepthData {
  screenName: string;
  maxDepth: number;
  avgDepth: number;
}

export default function MobileSessionDetailsPage() {
  const { t } = useTranslation(['mobile', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);

  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/analytics/mobile/journeys');
    }
  };

  const [session, setSession] = useState<MobileSessionInfo | null>(null);
  const [timeline, setTimeline] = useState<ScreenEvent[]>([]);
  const [touchEvents, setTouchEvents] = useState<TouchEvent[]>([]);
  const [heatmapPoints, setHeatmapPoints] = useState<TouchHeatmapPoint[]>([]);
  const [scrollDepths, setScrollDepths] = useState<ScrollDepthData[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeScreen, setActiveScreen] = useState<string>('');
  const [screens, setScreens] = useState<string[]>([]);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);

  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(120000);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [heatmapType, setHeatmapType] = useState<'all' | 'tap' | 'swipe' | 'scroll'>('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const animationFrameRef = useRef<number>();
  const lastTimeRef = useRef<number>(Date.now());

  // Handle ESC key to close
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, []);

  // Prevent scroll on body when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    if (id) {
      loadSessionData();
    }
  }, [id]);

  // Fetch screenshot when active screen changes
  useEffect(() => {
    const fetchScreenshot = async () => {
      if (!activeScreen || !selectedAppId || selectedAppId === 'all') {
        setScreenshot(null);
        return;
      }

      setScreenshotLoading(true);
      try {
        const result = await sdkService.getScreenshot(selectedAppId, activeScreen);
        if (result?.screenshot) {
          setScreenshot(result.screenshot);
        } else {
          setScreenshot(null);
        }
      } catch (error) {
        console.log('[MobileSessionDetails] No screenshot available for:', activeScreen);
        setScreenshot(null);
      } finally {
        setScreenshotLoading(false);
      }
    };

    fetchScreenshot();
  }, [activeScreen, selectedAppId]);

  useEffect(() => {
    if (isPlaying) {
      const animate = () => {
        const now = Date.now();
        const delta = (now - lastTimeRef.current) * playbackSpeed;
        lastTimeRef.current = now;

        setCurrentTime((prev) => {
          const newTime = prev + delta;
          if (newTime >= duration) {
            setIsPlaying(false);
            return duration;
          }
          return newTime;
        });

        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animationFrameRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    } else {
      lastTimeRef.current = Date.now();
    }
  }, [isPlaying, playbackSpeed, duration]);

  const loadSessionData = async () => {
    if (!id) return;

    try {
      setLoading(true);

      // Load session info and recording events in parallel
      const [sessionData, recordingEvents] = await Promise.all([
        rumService.getSession(id),
        rumService.getRecordingEvents(id).catch(() => []), // Don't fail if no recordings
      ]);

      setSession({
        id: sessionData.id,
        session_id: sessionData.session_id,
        user_id: sessionData.user_id,
        anonymous_id: sessionData.anonymous_id,
        platform: sessionData.platform,
        device_type: sessionData.device_type,
        device_model: sessionData.device_model,
        device_manufacturer: sessionData.device_manufacturer,
        os_name: sessionData.os_name,
        os_version: sessionData.os_version,
        app_version: sessionData.app_version,
        screen_width: sessionData.screen_width,
        screen_height: sessionData.screen_height,
        country: sessionData.country,
        city: sessionData.city,
        started_at: sessionData.started_at,
        ended_at: sessionData.ended_at,
        duration_ms: sessionData.duration_ms || 120000,
        screen_view_count: sessionData.page_view_count || 0,
        event_count: sessionData.event_count || 0,
        error_count: sessionData.error_count || 0,
        crash_count: sessionData.crash_count || 0,
      });

      const durationValue = sessionData.duration_ms || 120000;
      setDuration(durationValue);

      // Parse recording events into timeline format
      const sessionStartTime = new Date(sessionData.started_at).getTime();
      const events: ScreenEvent[] = [];
      const uniqueScreensSet = new Set<string>();
      const scrollDepthMap = new Map<string, { max: number; values: number[] }>();

      // Debug: Log recording events structure
      if (recordingEvents && recordingEvents.length > 0) {
        console.log('[MobileSessionDetails] Recording events received:', recordingEvents.length);
        console.log('[MobileSessionDetails] Session start time:', sessionStartTime, new Date(sessionStartTime).toISOString());
        console.log('[MobileSessionDetails] First event timestamp:', recordingEvents[0]?.timestamp);
        console.log('[MobileSessionDetails] Last event timestamp:', recordingEvents[recordingEvents.length - 1]?.timestamp);
        console.log('[MobileSessionDetails] First 5 events structure:', recordingEvents.slice(0, 5).map((e: any) => ({
          type: e.type,
          typeOf: typeof e.type,
          hasData: !!e.data,
          dataKeys: e.data ? Object.keys(e.data) : [],
          dataSource: e.data?.source,
          dataTag: e.data?.tag,
          timestamp: e.timestamp,
        })));

        // Count event types
        const eventTypeCounts = recordingEvents.reduce((acc: Record<string, number>, e: any) => {
          const key = typeof e.type === 'number' ? `rrweb-${e.type}` : String(e.type);
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});
        console.log('[MobileSessionDetails] Event type distribution:', eventTypeCounts);

        // Count rrweb sources for IncrementalSnapshot (type 3)
        const sourceCounts = recordingEvents
          .filter((e: any) => e.type === 3 && e.data?.source !== undefined)
          .reduce((acc: Record<string, number>, e: any) => {
            acc[`source-${e.data.source}`] = (acc[`source-${e.data.source}`] || 0) + 1;
            return acc;
          }, {});
        if (Object.keys(sourceCounts).length > 0) {
          console.log('[MobileSessionDetails] IncrementalSnapshot sources:', sourceCounts);
        }
      }

      // Process recording events
      if (recordingEvents && recordingEvents.length > 0) {
        let currentScreen = 'MainScreen';

        // rrweb event types (numeric)
        const RRWEB_TYPES = {
          DomContentLoaded: 0,
          Load: 1,
          FullSnapshot: 2,
          IncrementalSnapshot: 3,
          Meta: 4,
          Custom: 5,
        };

        // rrweb IncrementalSnapshot sources
        const RRWEB_SOURCES = {
          Mutation: 0,
          MouseMove: 1,
          MouseInteraction: 2,
          Scroll: 3,
          ViewportResize: 4,
          Input: 5,
          TouchMove: 6,
          MediaInteraction: 7,
          StyleSheetRule: 8,
          CanvasMutation: 9,
          Font: 10,
          Log: 11,
          Drag: 12,
          StyleDeclaration: 13,
        };

        // rrweb MouseInteraction types
        const RRWEB_MOUSE_INTERACTION = {
          MouseUp: 0,
          MouseDown: 1,
          Click: 2,
          ContextMenu: 3,
          DblClick: 4,
          Focus: 5,
          Blur: 6,
          TouchStart: 7,
          TouchMove_Departed: 8,
          TouchEnd: 9,
          TouchCancel: 10,
        };

        // Determine base timestamp from first event
        const firstEventTimestamp = recordingEvents[0]?.timestamp || sessionStartTime;
        const baseTimestamp = Math.min(firstEventTimestamp, sessionStartTime);

        recordingEvents.forEach((event: any, idx: number) => {
          // Calculate relative time from session/recording start
          let eventTime = idx * 1000; // fallback: spread events evenly
          if (event.timestamp) {
            // Handle both relative (small numbers) and absolute (Unix ms) timestamps
            if (event.timestamp > 1000000000000) {
              // Absolute Unix timestamp in ms
              eventTime = event.timestamp - baseTimestamp;
            } else if (event.timestamp > 1000000000) {
              // Absolute Unix timestamp in seconds
              eventTime = (event.timestamp * 1000) - baseTimestamp;
            } else {
              // Already relative timestamp
              eventTime = event.timestamp;
            }
          }

          // Handle rrweb numeric event types
          if (typeof event.type === 'number') {
            // FullSnapshot or Meta - treat as screen view
            if (event.type === RRWEB_TYPES.FullSnapshot || event.type === RRWEB_TYPES.Meta) {
              const screenName = event.data?.href || event.data?.screen || currentScreen;
              if (event.type === RRWEB_TYPES.FullSnapshot) {
                currentScreen = screenName;
                uniqueScreensSet.add(screenName);
                events.push({
                  id: `event-${idx}`,
                  type: 'screenView',
                  timestamp: Math.max(0, eventTime),
                  title: screenName,
                  screenName: screenName,
                  details: { source: 'FullSnapshot' },
                });
              }
            }
            // IncrementalSnapshot - contains interactions
            else if (event.type === RRWEB_TYPES.IncrementalSnapshot && event.data) {
              const source = event.data.source;

              // Mouse/Touch interaction
              if (source === RRWEB_SOURCES.MouseInteraction) {
                const interactionType = event.data.type;
                const isTouchEvent = interactionType >= RRWEB_MOUSE_INTERACTION.TouchStart;
                const isClick = interactionType === RRWEB_MOUSE_INTERACTION.Click ||
                               interactionType === RRWEB_MOUSE_INTERACTION.DblClick ||
                               interactionType === RRWEB_MOUSE_INTERACTION.TouchEnd;

                if (isClick || isTouchEvent) {
                  const x = event.data.x ?? 50;
                  const y = event.data.y ?? 50;
                  const touchType = isTouchEvent ? 'tap' : 'click';

                  events.push({
                    id: `event-${idx}`,
                    type: 'touch',
                    timestamp: Math.max(0, eventTime),
                    title: `${touchType.charAt(0).toUpperCase() + touchType.slice(1)} at (${Math.round(x)}, ${Math.round(y)})`,
                    screenName: currentScreen,
                    details: { type: touchType, interactionType },
                    x: (x / (sessionData.screen_width || 375)) * 100,
                    y: (y / (sessionData.screen_height || 812)) * 100,
                  });
                }
              }
              // Scroll
              else if (source === RRWEB_SOURCES.Scroll) {
                const scrollY = event.data.y || 0;
                const scrollDepth = Math.min(100, Math.round((scrollY / (sessionData.screen_height || 812)) * 100));

                if (!scrollDepthMap.has(currentScreen)) {
                  scrollDepthMap.set(currentScreen, { max: 0, values: [] });
                }
                const screenScroll = scrollDepthMap.get(currentScreen)!;
                screenScroll.values.push(scrollDepth);
                screenScroll.max = Math.max(screenScroll.max, scrollDepth);

                events.push({
                  id: `event-${idx}`,
                  type: 'touch',
                  timestamp: Math.max(0, eventTime),
                  title: `Scrolled to ${scrollDepth}%`,
                  screenName: currentScreen,
                  details: { type: 'scroll', scrollDepth },
                });
              }
              // Touch Move
              else if (source === RRWEB_SOURCES.TouchMove) {
                // Track touch moves as gestures
                const positions = event.data.positions || [];
                if (positions.length > 0) {
                  const firstPos = positions[0];
                  const lastPos = positions[positions.length - 1];
                  const deltaX = (lastPos?.x || 0) - (firstPos?.x || 0);
                  const deltaY = (lastPos?.y || 0) - (firstPos?.y || 0);

                  // Determine swipe direction
                  let gestureType = 'swipe';
                  if (Math.abs(deltaX) > Math.abs(deltaY)) {
                    gestureType = deltaX > 0 ? 'swipe right' : 'swipe left';
                  } else {
                    gestureType = deltaY > 0 ? 'swipe down' : 'swipe up';
                  }

                  events.push({
                    id: `event-${idx}`,
                    type: 'touch',
                    timestamp: Math.max(0, eventTime),
                    title: gestureType.charAt(0).toUpperCase() + gestureType.slice(1),
                    screenName: currentScreen,
                    details: { type: 'swipe', direction: gestureType },
                    x: ((firstPos?.x || 50) / (sessionData.screen_width || 375)) * 100,
                    y: ((firstPos?.y || 50) / (sessionData.screen_height || 812)) * 100,
                  });
                }
              }
            }
            // Custom event (type 5) - mobile SDK specific events
            else if (event.type === RRWEB_TYPES.Custom && event.data) {
              const customTag = event.data.tag || '';
              const payload = event.data.payload || {};

              if (customTag === 'screenView' || customTag === 'screen' || payload.screen) {
                const screenName = payload.screen || payload.screenName || `Screen ${idx}`;
                currentScreen = screenName;
                uniqueScreensSet.add(screenName);
                events.push({
                  id: `event-${idx}`,
                  type: 'screenView',
                  timestamp: Math.max(0, eventTime),
                  title: screenName,
                  screenName: screenName,
                  details: { renderTime: payload.renderTime || 0 },
                });
              } else if (customTag === 'touch' || customTag === 'gesture') {
                const touchType = payload.type || payload.gestureType || 'tap';
                const x = payload.x ?? payload.absoluteX ?? 50;
                const y = payload.y ?? payload.absoluteY ?? 50;

                events.push({
                  id: `event-${idx}`,
                  type: 'touch',
                  timestamp: Math.max(0, eventTime),
                  title: `${touchType.charAt(0).toUpperCase() + touchType.slice(1)} at (${Math.round(x)}, ${Math.round(y)})`,
                  screenName: currentScreen,
                  details: { type: touchType, ...payload },
                  x: (x / (sessionData.screen_width || 375)) * 100,
                  y: (y / (sessionData.screen_height || 812)) * 100,
                });
              } else if (customTag === 'error' || customTag === 'crash') {
                events.push({
                  id: `event-${idx}`,
                  type: customTag,
                  timestamp: Math.max(0, eventTime),
                  title: payload.message || 'Error occurred',
                  screenName: currentScreen,
                  details: payload,
                });
              } else if (customTag === 'network' || customTag === 'networkRequest') {
                events.push({
                  id: `event-${idx}`,
                  type: 'networkRequest',
                  timestamp: Math.max(0, eventTime),
                  title: `${payload.method || 'GET'} ${payload.url || 'API Request'}`,
                  screenName: currentScreen,
                  details: { status: payload.status, duration: payload.duration },
                });
              }
            }
          }
          // Handle string event types from mobile SDK (original format)
          else if (event.type === 'screenView' || event.type === 'screen' || event.data?.screen) {
            const screenName = event.data?.screen || event.data?.screenName || event.screenName || `Screen ${idx}`;
            currentScreen = screenName;
            uniqueScreensSet.add(screenName);
            events.push({
              id: `event-${idx}`,
              type: 'screenView',
              timestamp: Math.max(0, eventTime),
              title: screenName,
              screenName: screenName,
              details: { renderTime: event.data?.renderTime || 0 },
            });
          } else if (event.type === 'touch' || event.type === 'gesture' || event.data?.touch) {
            const touchData = event.data?.touch || event.data || {};
            const touchType = touchData.type || touchData.gestureType || 'tap';
            const x = touchData.x ?? touchData.absoluteX ?? Math.random() * 80 + 10;
            const y = touchData.y ?? touchData.absoluteY ?? Math.random() * 80 + 10;

            events.push({
              id: `event-${idx}`,
              type: 'touch',
              timestamp: Math.max(0, eventTime),
              title: `${touchType.charAt(0).toUpperCase() + touchType.slice(1)} at (${Math.round(x)}, ${Math.round(y)})`,
              screenName: currentScreen,
              details: {
                type: touchType,
                elementType: touchData.target || touchData.elementType,
                elementLabel: touchData.label || touchData.accessibilityLabel,
              },
              x: typeof x === 'number' ? (x / (sessionData.screen_width || 375)) * 100 : x,
              y: typeof y === 'number' ? (y / (sessionData.screen_height || 812)) * 100 : y,
            });
          } else if (event.type === 'scroll' || event.data?.scroll) {
            const scrollData = event.data?.scroll || event.data || {};
            const scrollDepth = scrollData.scrollDepth || scrollData.contentOffset?.y || 0;

            if (!scrollDepthMap.has(currentScreen)) {
              scrollDepthMap.set(currentScreen, { max: 0, values: [] });
            }
            const screenScroll = scrollDepthMap.get(currentScreen)!;
            screenScroll.values.push(scrollDepth);
            screenScroll.max = Math.max(screenScroll.max, scrollDepth);

            events.push({
              id: `event-${idx}`,
              type: 'touch',
              timestamp: Math.max(0, eventTime),
              title: `Scrolled to ${Math.round(scrollDepth)}%`,
              screenName: currentScreen,
              details: { type: 'scroll', scrollDepth },
            });
          } else if (event.type === 'error' || event.type === 'crash') {
            events.push({
              id: `event-${idx}`,
              type: event.type,
              timestamp: Math.max(0, eventTime),
              title: event.data?.message || event.message || 'Error occurred',
              screenName: currentScreen,
              details: event.data || {},
            });
          } else if (event.type === 'network' || event.type === 'networkRequest') {
            const netData = event.data || {};
            events.push({
              id: `event-${idx}`,
              type: 'networkRequest',
              timestamp: Math.max(0, eventTime),
              title: `${netData.method || 'GET'} ${netData.url || 'API Request'}`,
              screenName: currentScreen,
              details: { status: netData.status, duration: netData.duration },
            });
          }
        });
      }

      // Debug: Log parsed events
      console.log('[MobileSessionDetails] Parsed events:', events.length);
      console.log('[MobileSessionDetails] Parsed event types:', events.reduce((acc: Record<string, number>, e) => {
        acc[e.type] = (acc[e.type] || 0) + 1;
        return acc;
      }, {}));
      console.log('[MobileSessionDetails] Unique screens:', Array.from(uniqueScreensSet));

      // If no events were parsed, show message
      if (events.length === 0) {
        console.warn('[MobileSessionDetails] No events could be parsed from recording data');
        events.push({
          id: 'no-events',
          type: 'screenView',
          timestamp: 0,
          title: 'Session Started',
          screenName: 'App',
          details: { note: 'No detailed events recorded for this session' },
        });
        uniqueScreensSet.add('App');
      }

      // Sort events by timestamp
      events.sort((a, b) => a.timestamp - b.timestamp);
      setTimeline(events);

      // Set screens
      const uniqueScreens = Array.from(uniqueScreensSet);
      setScreens(uniqueScreens);
      if (uniqueScreens.length > 0) {
        setActiveScreen(uniqueScreens[0]);
      }

      // Generate touch events from timeline
      const touches: TouchEvent[] = events
        .filter(e => e.type === 'touch' && (e.x !== undefined || e.y !== undefined))
        .map((e, idx) => ({
          id: `touch-${idx}`,
          type: e.details?.type || 'tap',
          timestamp: e.timestamp,
          screenName: e.screenName || '',
          x: e.x || 50,
          y: e.y || 50,
          relativeX: (e.x || 50) / 100,
          relativeY: (e.y || 50) / 100,
          details: e.details,
        }));
      setTouchEvents(touches);

      // Generate heatmap points from real touch data
      const points: TouchHeatmapPoint[] = touches
        .filter(touch => touch.x && touch.y)
        .map(touch => ({
          x: touch.x,
          y: touch.y,
          type: (touch.type as 'tap' | 'longPress' | 'swipe' | 'pinch') || 'tap',
          intensity: touch.type === 'tap' ? 0.9 : touch.type === 'longPress' ? 1 : 0.7,
          screenName: touch.screenName,
        }));
      setHeatmapPoints(points);

      // Generate scroll depth data from real data
      const scrollData: ScrollDepthData[] = uniqueScreens.map(screen => {
        const screenScrollData = scrollDepthMap.get(screen);
        if (screenScrollData && screenScrollData.values.length > 0) {
          const avgDepth = screenScrollData.values.reduce((a, b) => a + b, 0) / screenScrollData.values.length;
          return {
            screenName: screen,
            maxDepth: Math.min(100, screenScrollData.max),
            avgDepth: Math.min(100, Math.round(avgDepth)),
          };
        }
        return {
          screenName: screen,
          maxDepth: 50,
          avgDepth: 30,
        };
      });
      setScrollDepths(scrollData);

    } catch (error) {
      console.error('Failed to load session:', error);
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSkip = (seconds: number) => {
    setCurrentTime(prev => Math.max(0, Math.min(duration, prev + seconds * 1000)));
  };

  const handleRestart = () => {
    setCurrentTime(0);
    setIsPlaying(true);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    setCurrentTime(percentage * duration);
    if (isPlaying) {
      setIsPlaying(false);
    }
  };

  const handleEventClick = (event: ScreenEvent) => {
    setCurrentTime(event.timestamp);
    if (event.screenName) {
      setActiveScreen(event.screenName);
    }
  };

  const handleDeleteRecording = async () => {
    if (!session?.session_id) return;

    try {
      setIsDeleting(true);
      await rumService.deleteRecording(session.session_id);
      setShowDeleteModal(false);
      handleClose();
    } catch (error) {
      console.error('Failed to delete recording:', error);
      toast.error(t('errors.deleteRecordingFailed') || 'Failed to delete recording');
      setIsDeleting(false);
    }
  };

  const getEventIcon = (type: string, details?: any) => {
    switch (type) {
      case 'screenView':
        return (
          <div className="p-1.5 bg-blue-500/20 rounded">
            <DevicePhoneMobileIcon className="w-4 h-4 text-blue-400" />
          </div>
        );
      case 'touch':
        const touchType = details?.type;
        if (touchType === 'tap' || touchType === 'longPress') {
          return (
            <div className="p-1.5 bg-purple-500/20 rounded">
              <HandRaisedIcon className="w-4 h-4 text-purple-400" />
            </div>
          );
        }
        if (touchType === 'swipe') {
          return (
            <div className="p-1.5 bg-green-500/20 rounded">
              <ArrowsPointingOutIcon className="w-4 h-4 text-green-400" />
            </div>
          );
        }
        if (touchType === 'scroll') {
          return (
            <div className="p-1.5 bg-cyan-500/20 rounded">
              <ArrowsUpDownIcon className="w-4 h-4 text-cyan-400" />
            </div>
          );
        }
        return (
          <div className="p-1.5 bg-purple-500/20 rounded">
            <HandRaisedIcon className="w-4 h-4 text-purple-400" />
          </div>
        );
      case 'error':
      case 'crash':
        return (
          <div className="p-1.5 bg-red-500/20 rounded">
            <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        );
      case 'networkRequest':
        return (
          <div className="p-1.5 bg-yellow-500/20 rounded">
            <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getTouchColor = (type: string, intensity: number): string => {
    const alpha = intensity * 0.8;
    switch (type) {
      case 'tap':
        return `rgba(168, 85, 247, ${alpha})`; // purple
      case 'longPress':
        return `rgba(239, 68, 68, ${alpha})`; // red
      case 'swipe':
        return `rgba(34, 197, 94, ${alpha})`; // green
      case 'pinch':
        return `rgba(59, 130, 246, ${alpha})`; // blue
      default:
        return `rgba(168, 85, 247, ${alpha})`;
    }
  };

  const filteredHeatmapPoints = heatmapPoints.filter(point => {
    if (point.screenName !== activeScreen) return false;
    if (heatmapType === 'all') return true;
    if (heatmapType === 'tap') return point.type === 'tap' || point.type === 'longPress';
    if (heatmapType === 'swipe') return point.type === 'swipe' || point.type === 'pinch';
    return true;
  });

  const activeScrollDepth = scrollDepths.find(s => s.screenName === activeScreen);

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'ios': return '🍎';
      case 'android': return '🤖';
      case 'react-native': return '⚛️';
      case 'flutter': return '🦋';
      default: return '📱';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="text-center flex flex-col items-center justify-center">
          <div className="relative w-16 h-16 mb-6">
            <div className="absolute inset-0 w-16 h-16 border-4 border-gray-800 rounded-full"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-purple-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-400 text-lg font-medium">{t('session.loading') || 'Loading session...'}</p>
          <p className="mt-2 text-gray-600 text-sm">{t('session.preparingReplay') || 'Preparing session replay'}</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="text-center">
          <p className="text-gray-400 text-lg">{t('session.notFound') || 'Session not found'}</p>
          <button
            onClick={handleClose}
            className="mt-4 px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            {t('common:actions.back') || 'Back'}
          </button>
        </div>
      </div>
    );
  }

  const progress = (currentTime / duration) * 100;
  const activeEvents = timeline.filter(e => e.timestamp <= currentTime);
  const nextEvent = timeline.find(e => e.timestamp > currentTime);

  return (
    <div
      className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex flex-col transition-opacity duration-300"
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-b from-gray-900/90 to-black/90 border-b border-gray-800">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors group"
            title="Close (ESC)"
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 group-hover:text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <span>{getPlatformIcon(session.platform)}</span>
              {t('session.mobileReplay') || 'Mobile Session Replay'}
            </h1>
            <p className="text-sm text-gray-400">
              <span className="text-purple-400 font-mono">{session.session_id.substring(0, 8)}</span>
              {session.user_id && <span className="ml-3">User: {session.user_id}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          {/* Heatmap Controls */}
          <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setHeatmapType('all')}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                heatmapType === 'all' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setHeatmapType('tap')}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                heatmapType === 'tap' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Taps
            </button>
            <button
              onClick={() => setHeatmapType('swipe')}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                heatmapType === 'swipe' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Gestures
            </button>
            <button
              onClick={() => setHeatmapType('scroll')}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                heatmapType === 'scroll' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Scroll
            </button>
          </div>
          <button
            onClick={() => setShowHeatmap(!showHeatmap)}
            className={`px-4 py-2 rounded-lg transition-all ${
              showHeatmap
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {showHeatmap ? '🔥 Heatmap Active' : 'Show Heatmap'}
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2"
          >
            <TrashIcon className="w-5 h-5" />
            <span>{t('session.delete') || 'Delete'}</span>
          </button>
          <div className="text-right">
            <p className="text-sm text-gray-400">{t('session.duration') || 'Duration'}</p>
            <p className="text-white font-semibold">{formatTime(session.duration_ms || 0)}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Player Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 overflow-auto">
          <div className="relative w-full max-w-md">
            {/* Screen Selector */}
            <div className="mb-4 flex flex-wrap gap-2 justify-center">
              {screens.map(screen => (
                <button
                  key={screen}
                  onClick={() => setActiveScreen(screen)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    activeScreen === screen
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {screen}
                </button>
              ))}
            </div>

            {/* Simulated Phone Frame */}
            <div className="bg-gray-900 rounded-[3rem] p-3 shadow-2xl border-4 border-gray-800">
              {/* Phone Notch */}
              <div className="bg-black rounded-[2.5rem] overflow-hidden relative">
                {/* Status Bar */}
                <div className="bg-black h-8 flex items-center justify-between px-6 text-white text-xs">
                  <span>9:41</span>
                  <div className="absolute left-1/2 -translate-x-1/2 top-1 w-20 h-5 bg-black rounded-b-xl"></div>
                  <span>100% 🔋</span>
                </div>

                {/* Screen Content */}
                <div className="relative bg-gray-100 aspect-[9/19] overflow-hidden">
                  {/* Real Screenshot or Placeholder */}
                  {screenshotLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  ) : screenshot ? (
                    <img
                      src={screenshot}
                      alt={`Screenshot of ${activeScreen}`}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 p-4">
                      <DevicePhoneMobileIcon className="w-16 h-16 text-gray-600 mb-4" />
                      <div className="text-center">
                        <p className="text-gray-400 font-medium mb-2">{activeScreen || 'Mobile Screen'}</p>
                        <p className="text-gray-500 text-xs max-w-[200px]">
                          {t('mobile:session.noScreenshot', 'Screenshot not available. Enable screenshot capture in the mobile SDK.')}
                        </p>
                      </div>
                      {/* Visual hint showing this is a heatmap visualization */}
                      <div className="mt-4 px-3 py-1 bg-purple-600/20 rounded-full">
                        <span className="text-purple-400 text-xs">{t('mobile:session.heatmapView', 'Heatmap View')}</span>
                      </div>
                    </div>
                  )}

                  {/* Touch Heatmap Overlay */}
                  {showHeatmap && (
                    <div className="absolute inset-0 pointer-events-none">
                      {filteredHeatmapPoints.map((point, index) => (
                        <div
                          key={index}
                          className="absolute animate-pulse"
                          style={{
                            left: `${point.x}%`,
                            top: `${point.y}%`,
                            width: point.type === 'longPress' ? '60px' : '50px',
                            height: point.type === 'longPress' ? '60px' : '50px',
                            transform: 'translate(-50%, -50%)',
                            background: `radial-gradient(circle, ${getTouchColor(point.type, point.intensity)} 0%, transparent 70%)`,
                            filter: 'blur(10px)',
                            opacity: point.intensity,
                          }}
                        ></div>
                      ))}
                    </div>
                  )}

                  {/* Scroll Depth Indicator */}
                  {showHeatmap && heatmapType === 'scroll' && activeScrollDepth && (
                    <div className="absolute right-0 top-0 bottom-0 w-2 bg-gradient-to-b from-green-500 via-yellow-500 to-red-500 opacity-50">
                      <div
                        className="absolute left-0 right-0 bg-red-500"
                        style={{
                          top: `${100 - activeScrollDepth.maxDepth}%`,
                          bottom: 0,
                          opacity: 0.3,
                        }}
                      ></div>
                      <div
                        className="absolute left-0 right-0 h-1 bg-white shadow-lg"
                        style={{ top: `${100 - activeScrollDepth.avgDepth}%` }}
                      ></div>
                    </div>
                  )}

                  {/* Touch Cursor Animation */}
                  <div
                    className="absolute w-8 h-8 pointer-events-none transition-all duration-200"
                    style={{
                      left: `${30 + (currentTime / duration) * 40}%`,
                      top: `${20 + Math.sin(currentTime / 1000) * 30}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <div className="w-full h-full bg-purple-500/50 rounded-full animate-ping"></div>
                    <div className="absolute inset-1 bg-purple-600 rounded-full"></div>
                  </div>
                </div>

                {/* Home Indicator */}
                <div className="bg-black h-8 flex items-center justify-center">
                  <div className="w-32 h-1 bg-gray-700 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Scroll Depth Legend */}
            {showHeatmap && heatmapType === 'scroll' && activeScrollDepth && (
              <div className="mt-4 bg-gray-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-2">Scroll Depth: {activeScreen}</h4>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-gray-400">Avg: {activeScrollDepth.avgDepth}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-gray-400">Max: {activeScrollDepth.maxDepth}%</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="w-full max-w-2xl mt-6 space-y-4">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div
                className="relative h-2 bg-gray-800 rounded-full cursor-pointer group"
                onClick={handleSeek}
              >
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                ></div>

                {/* Event Markers */}
                {timeline.map((event) => (
                  <div
                    key={event.id}
                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full cursor-pointer"
                    style={{
                      left: `${(event.timestamp / duration) * 100}%`,
                      backgroundColor:
                        event.type === 'error' || event.type === 'crash'
                          ? '#ef4444'
                          : event.type === 'touch'
                          ? '#a855f7'
                          : event.type === 'screenView'
                          ? '#3b82f6'
                          : '#10b981',
                    }}
                    title={event.title}
                  ></div>
                ))}

                {/* Playhead */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg transition-all"
                  style={{ left: `${progress}%` }}
                ></div>
              </div>

              <div className="flex justify-between text-sm text-gray-400">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleRestart}
                  className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
                >
                  <ArrowPathIcon className="w-5 h-5 text-gray-400 group-hover:text-white" />
                </button>
                <button
                  onClick={() => handleSkip(-10)}
                  className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
                >
                  <BackwardIcon className="w-5 h-5 text-gray-400 group-hover:text-white" />
                </button>
                <button
                  onClick={handlePlayPause}
                  className="p-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg transition-all shadow-lg shadow-purple-500/30"
                >
                  {isPlaying ? (
                    <PauseIcon className="w-6 h-6 text-white" />
                  ) : (
                    <PlayIcon className="w-6 h-6 text-white" />
                  )}
                </button>
                <button
                  onClick={() => handleSkip(10)}
                  className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
                >
                  <ForwardIcon className="w-5 h-5 text-gray-400 group-hover:text-white" />
                </button>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-400">Speed:</span>
                  {[0.5, 1, 1.5, 2].map((speed) => (
                    <button
                      key={speed}
                      onClick={() => setPlaybackSpeed(speed)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        playbackSpeed === speed
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Events Timeline */}
        <div className="w-96 bg-gray-900 border-l border-gray-800 flex flex-col">
          <div className="p-4 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white">{t('session.timeline') || 'Event Timeline'}</h2>
            <p className="text-sm text-gray-400 mt-1">
              {activeEvents.length} of {timeline.length} events
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {timeline.map((event) => {
              const isPast = event.timestamp <= currentTime;
              const isActive = nextEvent?.id === event.id;

              return (
                <button
                  key={event.id}
                  onClick={() => handleEventClick(event)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-purple-600 shadow-lg shadow-purple-500/30'
                      : isPast
                      ? 'bg-gray-800 hover:bg-gray-750'
                      : 'bg-gray-800/40 hover:bg-gray-800/60'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">{getEventIcon(event.type, event.details)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-medium uppercase tracking-wide ${
                          isPast ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {event.type === 'touch' ? event.details?.type || 'touch' : event.type}
                        </span>
                        <span className={`text-xs ${isPast ? 'text-gray-400' : 'text-gray-600'}`}>
                          {formatTime(event.timestamp)}
                        </span>
                      </div>
                      <p className={`text-sm font-medium ${
                        isActive ? 'text-white' : isPast ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {event.title}
                      </p>
                      {event.screenName && (
                        <p className={`text-xs mt-1 ${
                          isActive ? 'text-purple-200' : isPast ? 'text-gray-500' : 'text-gray-700'
                        }`}>
                          📱 {event.screenName}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Session Info */}
          <div className="p-4 border-t border-gray-800 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-gray-500">Platform</p>
                <p className="text-white font-medium">{getPlatformIcon(session.platform)} {session.platform}</p>
              </div>
              <div>
                <p className="text-gray-500">Device</p>
                <p className="text-white font-medium">{session.device_model || session.device_type || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-gray-500">OS</p>
                <p className="text-white font-medium">{session.os_name} {session.os_version}</p>
              </div>
              <div>
                <p className="text-gray-500">App Version</p>
                <p className="text-white font-medium">{session.app_version || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-500">Screens</p>
                <p className="text-white font-medium">{session.screen_view_count}</p>
              </div>
              <div>
                <p className="text-gray-500">Errors</p>
                <p className={`font-medium ${session.error_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {session.error_count}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 border border-gray-700">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center">
                <TrashIcon className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {t('session.deleteTitle') || 'Delete Session'}
                </h3>
                <p className="text-gray-400 text-sm">
                  {t('session.deleteConfirm') || 'Are you sure you want to delete this session? This action cannot be undone.'}
                </p>
              </div>
            </div>

            <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
              <p className="text-xs text-gray-500 mb-1">Session ID</p>
              <p className="text-white font-mono text-sm">{session?.session_id}</p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {t('common:actions.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleDeleteRecording}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t('session.deleting') || 'Deleting...'}</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    <span>{t('session.delete') || 'Delete'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
